/**
 * SearchField — the one search control across Vault: a text input with a
 * VoiceButton beside it, removable filter chips, and a one-line AI read-back of
 * what the query resolved to (the SEARCH / query-parse job). Dictation fills the
 * same field it sits next to; typing always works.
 *
 * The read-back is real: as you type (debounced) it POSTs to
 * /budget/parse-query/, which reshapes ai_service.jobs.parse_query into
 * {filters, readback}. Filters render as removable chips (a chip with a token
 * strips those words from the query); the read-back reads plainly, e.g.
 * "Showing over R500, at Checkers — 3 rows". AI never blocks: on an empty
 * result or any failure the field is still a plain substring search, and hosts
 * can override `aiLine`/`chips` to supply their own. Pass `count` (rows shown)
 * to have the read-back tell you how many matched.
 *
 * A host that filters its own rows (the Recurring list, #300) passes
 * `onFilters`: the parse then still runs even though the host supplies its own
 * `aiLine`/`chips`, and the host receives the parse's active (not dismissed)
 * filters — each with its `value` — plus the leftover words, or null when there
 * is no AI parse. `shouldParse(text)` lets the host skip the AI call when a
 * plain substring match already answers the query.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import VoiceButton from './VoiceButton.jsx';

// Remove a filter's words from the query, case-insensitively, tidying spaces.
function stripToken(value, token) {
  if (!token) return value;
  const i = value.toLowerCase().indexOf(token.toLowerCase());
  if (i < 0) return value;
  return (value.slice(0, i) + value.slice(i + token.length)).replace(/\s+/g, ' ').trim();
}

export default function SearchField({
  value, onChange, placeholder = 'Search…', aiLine, chips, onVoice, onVoiceState,
  size = 44, autoFocus, parse = true, count, onFilters, shouldParse,
}) {
  const [parsed, setParsed] = useState(null);      // { filters, readback, ai }
  const [dismissed, setDismissed] = useState([]);  // chip keys the user removed
  const seq = useRef(0);

  // Debounced query-parse. A stale response (an older keystroke) is dropped via
  // the sequence guard so the chips always match the latest text.
  useEffect(() => {
    // Skip when a host supplies its own read-back/chips — no wasted AI calls —
    // unless the host asked for the filters themselves (onFilters).
    if (!parse || ((aiLine != null || chips != null) && !onFilters)) return undefined;
    const text = (value || '').trim();
    setDismissed([]);
    if (text.length < 2) { setParsed(null); return undefined; }
    // The host's plain substring pass already answers this query: no AI call.
    if (shouldParse && !shouldParse(text)) { seq.current += 1; setParsed(null); return undefined; }
    const id = ++seq.current;
    const t = setTimeout(() => {
      api('/budget/parse-query/', { method: 'POST', body: { text } })
        .then((d) => { if (id === seq.current) setParsed(d); })
        .catch(() => { if (id === seq.current) setParsed(null); });
    }, 500);
    return () => clearTimeout(t);
    // shouldParse/onFilters are read fresh each keystroke; they are callbacks,
    // not triggers, so they stay out of the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, parse, onFilters ? null : aiLine, onFilters ? null : chips]);

  // Tell a filtering host what the parse means right now (minus dismissed chips).
  useEffect(() => {
    if (!onFilters) return;
    const active = (parsed?.ai && parsed?.filters?.length)
      ? parsed.filters.filter((f) => !dismissed.includes(f.key)) : null;
    onFilters(active && active.length ? { filters: active, text: parsed.text || '' } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed, dismissed]);

  // Host-supplied chips win; otherwise derive them from the AI parse. A chip
  // with a token strips those words from the query; one without just dismisses.
  const hostChips = Array.isArray(chips) && chips.length > 0;
  const aiChips = (parsed?.filters || [])
    .filter((f) => !dismissed.includes(f.key))
    .map((f) => ({
      key: f.key, label: f.label,
      onRemove: () => (f.token
        ? onChange?.(stripToken(value, f.token))
        : setDismissed((d) => [...d, f.key])),
    }));
  const shownChips = hostChips ? chips : aiChips;

  // Host-supplied read-back wins; otherwise the AI line, with the row count when
  // the host passed one ("Showing over R500, at Checkers — 3 rows").
  const rows = count == null ? '' : ` — ${count} row${count === 1 ? '' : 's'}`;
  const shownLine = aiLine != null ? aiLine
    : (parsed?.readback ? parsed.readback + rows : '');

  return (
    <div className="w-full">
      <div className="flex items-center gap-2">
        <input
          className="input flex-1"
          type="search"
          value={value}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onChange={(e) => onChange?.(e.target.value)}
        />
        <VoiceButton size={size} onText={(t) => (onVoice ? onVoice(t) : onChange?.(t))} onState={onVoiceState} />
      </div>
      {shownChips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {shownChips.map((c, i) => (
            <button key={c.key ?? c.label ?? i} type="button"
                    className="inline-flex items-center gap-1 rounded-full border text-xs px-2.5 py-1"
                    style={{ borderColor: 'var(--color-accent)', color: 'var(--color-accent)', background: 'color-mix(in srgb, var(--color-accent) 12%, transparent)' }}
                    onClick={() => c.onRemove?.(c)}>
              <span className="min-w-0">{c.label}</span><span aria-hidden className="opacity-70">✕</span>
            </button>
          ))}
        </div>
      )}
      {shownLine && (
        <div className="text-xs mt-1.5 flex items-start gap-1"
             style={{ color: 'var(--color-accent-400)' }}>
          <span aria-hidden>✨</span><span className="min-w-0">{shownLine}</span>
        </div>
      )}
    </div>
  );
}
